import { useState } from "react";
import { createSite, listSites, updateSite } from "./api";
import type { Site } from "./types";
import { useAdminResource } from "./use-admin-resource";
import { EmptyRow } from "../dashboard/ui";
import { useToast } from "../../components/useToast";

export function SitesPanel() {
  const { data: sites, loading, error, refresh } = useAdminResource(() => listSites(), []);
  const { showSuccess, showError } = useToast();

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [address, setAddress] = useState("");
  const [createPending, setCreatePending] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: "", code: "", address: "" });
  const [editPending, setEditPending] = useState(false);

  function startEdit(site: Site) {
    setEditingId(site.id);
    setEditForm({ name: site.name, code: site.code ?? "", address: site.address ?? "" });
  }

  async function submitEdit(siteId: number) {
    if (!editForm.name.trim()) {
      showError("Site name is required");
      return;
    }
    setEditPending(true);
    try {
      await updateSite(siteId, {
        name: editForm.name.trim(),
        code: editForm.code.trim() || null,
        address: editForm.address.trim() || null,
      });
      showSuccess("Site updated");
      setEditingId(null);
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to update site");
    } finally {
      setEditPending(false);
    }
  }

  async function submitCreate() {
    if (!name.trim()) {
      showError("Site name is required");
      return;
    }
    setCreatePending(true);
    try {
      await createSite({
        name: name.trim(),
        code: code.trim() || null,
        address: address.trim() || null,
        is_active: true,
      });
      showSuccess(`Created site "${name.trim()}"`);
      setName("");
      setCode("");
      setAddress("");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to create site");
    } finally {
      setCreatePending(false);
    }
  }

  async function toggleActive(site: Site) {
    setBusyId(site.id);
    try {
      await updateSite(site.id, { is_active: !site.is_active });
      showSuccess(site.is_active ? `Deactivated "${site.name}"` : `Activated "${site.name}"`);
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to update site");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Sites</h2>
        <span>{sites ? `${sites.length} total` : ""}</span>
      </header>
      <p className="management-note">
        Sites are your company's physical locations or branches. Assign a security guard or host to
        a site and they only see and act on that site's visitors. Users left unassigned are
        company-wide and see every site. Deactivate a site instead of deleting it so its past
        visits stay intact.
      </p>

      {error ? <p className="error-banner">{error}</p> : null}

      <form
        className="action-form action-card"
        onSubmit={(event) => {
          event.preventDefault();
          void submitCreate();
        }}
      >
        <div className="checkin-fields">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Site name (e.g. Head Office)"
            aria-label="Site name"
            disabled={loading}
          />
          <input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="Code (optional, e.g. HQ)"
            aria-label="Site code"
            disabled={loading}
          />
          <input
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            placeholder="Address (optional)"
            aria-label="Site address"
            disabled={loading}
          />
        </div>
        <button className="refresh-btn action-submit" type="submit" disabled={createPending || loading}>
          {createPending ? "Adding..." : "Add Site"}
        </button>
      </form>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Address</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {sites && sites.length > 0 ? (
              sites.map((site) =>
                editingId === site.id ? (
                  <tr key={`site-edit-${site.id}`}>
                    <td colSpan={5}>
                      <div className="checkin-fields">
                        <input
                          value={editForm.name}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
                          placeholder="Site name *"
                          aria-label="Edit site name"
                        />
                        <input
                          value={editForm.code}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, code: event.target.value }))}
                          placeholder="Code (optional)"
                          aria-label="Edit site code"
                        />
                        <input
                          value={editForm.address}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, address: event.target.value }))}
                          placeholder="Address (optional)"
                          aria-label="Edit site address"
                        />
                        <div className="action-cell">
                          <button
                            className="refresh-btn"
                            type="button"
                            disabled={editPending}
                            onClick={() => void submitEdit(site.id)}
                          >
                            {editPending ? "Saving..." : "Save"}
                          </button>
                          <button
                            className="refresh-btn danger-action-btn"
                            type="button"
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={site.id}>
                    <td>{site.name}</td>
                    <td>{site.code || "—"}</td>
                    <td>{site.address || "—"}</td>
                    <td>
                      <span className={site.is_active ? "status-pill status-approved" : "status-pill status-rejected"}>
                        {site.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="action-cell">
                        <button className="refresh-btn" type="button" onClick={() => startEdit(site)}>
                          Edit
                        </button>
                        <button
                          className="refresh-btn"
                          type="button"
                          onClick={() => void toggleActive(site)}
                          disabled={busyId === site.id}
                        >
                          {site.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )
            ) : (
              <EmptyRow label={loading ? "Loading..." : "No sites yet."} colSpan={5} />
            )}
          </tbody>
        </table>
      </div>
    </article>
  );
}
